import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { UpdateSystemUserRequest } from '../../api/system-user/system-user.types';
import { RootState } from '../../types/common.types';
import { TrackingDeviceAssignment, User } from '../../types/entity.types';

import { useDeviceAssignmentApi } from '../../api/device-assignment/useDeviceAssignmentApi';
import { useSystemUserApi } from '../../api/system-user/useSystemUserApi';

import { updateSystemUserDeviceId } from '../../slices/system/system.slice';

import { retrieveFromLocalStorage } from '../../utils/util';

import Profile from '../../components/common/profile/Profile';

type Props = {
  show: boolean;
};

const ProfileContainer = ({ show }: Props) => {
  const { user } = useSelector(({ system }: RootState) => system);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { viewSystemUser, updateSystemUser: updateSystemUserRequest } = useSystemUserApi();

  const {
    createTrackingDeviceAssignment: createTrackingDeviceAssignmentRequest,
    retrieveTrackingDeviceAssignments: retrieveTrackingDeviceAssignmentsRequest
  } = useDeviceAssignmentApi();

  useEffect(() => {
    if (!user) {
      const systemUserExternalReference = retrieveFromLocalStorage('user');
      if (systemUserExternalReference) {
        viewSystemUser({ systemUserExternalReference });
      }
    }
  }, [viewSystemUser, user]);

  const signOut = () => {
    localStorage.clear();
    navigate('/');
  };

  const updateSystemUser = (user: User) => {
    updateSystemUserRequest(user as UpdateSystemUserRequest);
  };

  const createTrackingDeviceAssignment = (
    systemUserExternalReference: string,
    deviceId: string,
    onSuccess?: (data: TrackingDeviceAssignment) => void
  ) => {
    createTrackingDeviceAssignmentRequest({ systemUserExternalReference, deviceId }, (data) => {
      dispatch(updateSystemUserDeviceId(deviceId));

      onSuccess?.(data);
    });
  };

  const retrieveTrackingDeviceAssignments = (
    systemUserExternalReference: string,
    onSuccess?: (data: TrackingDeviceAssignment[]) => void
  ) => {
    retrieveTrackingDeviceAssignmentsRequest({ systemUserExternalReference }, onSuccess);
  };

  return (
    <Profile
      show={show}
      user={user}
      signOut={signOut}
      updateSystemUser={updateSystemUser}
      createTrackingDeviceAssignment={createTrackingDeviceAssignment}
      retrieveTrackingDeviceAssignments={retrieveTrackingDeviceAssignments}
    />
  );
};

export default ProfileContainer;
